import {
  customStringMinMaxValidation,
  ErrorPresentation,
  notEmptyArrayValidation,
  requiredValidation,
  stringMaxValidation,
  stringMinMaxValidation,
} from 'helpers/validators';
import { Dispatch, SetStateAction } from 'react';
import {
  companyAddressCountryFieldKey,
  companyAddressTextFieldKey,
  isCompanyAddressStarted,
} from 'shared/domain/company/mapping';
import {
  CompanyAddressKind,
  companyAddressKinds,
  CompanyAddressTextPart,
  companyAddressTextParts,
} from 'shared/domain/company/types/model';
import type { CompanyAddressesOnView, CompanyOnView } from 'shared/domain/company/types/view';
import { HashMap } from 'shared/types/commonView';
import { FieldSize } from 'shared/types/fieldSize';
import { ValidationSchema } from '../types';
import { onViewValidation } from '../validation';

/** Same limit as the API's address schema. */
const POSTAL_CODE_MIN_LENGTH = 3;

const addressTextPartValidation: Record<CompanyAddressTextPart, (value: unknown) => ErrorPresentation> = {
  streetAndNumber: (value) => stringMaxValidation(FieldSize.M, value),
  postalCode: (value) => (value ? customStringMinMaxValidation(POSTAL_CODE_MIN_LENGTH, FieldSize.S, value) : undefined),
  city: (value) => stringMaxValidation(FieldSize.M, value),
};

/** The API takes an address only complete: once any part is filled, every part is required. */
function addressPartValidation(
  kind: CompanyAddressKind,
  partValidation: (value: unknown) => ErrorPresentation = () => undefined
) {
  return (value: unknown, allValues?: CompanyAddressesOnView): ErrorPresentation =>
    (isCompanyAddressStarted(kind, allValues) ? requiredValidation(value) : undefined) || partValidation(value);
}

const addressValidation: ValidationSchema<CompanyAddressesOnView> = Object.assign(
  {},
  ...companyAddressKinds.map((kind) => ({
    [companyAddressCountryFieldKey(kind)]: addressPartValidation(kind),
    ...Object.fromEntries(
      companyAddressTextParts.map((part) => [
        companyAddressTextFieldKey(kind, part),
        addressPartValidation(kind, addressTextPartValidation[part]),
      ])
    ),
  }))
);

const validation: ValidationSchema<CompanyOnView> = {
  ...onViewValidation,
  ...addressValidation,
  shortLabel: shortLabelValidation,
  longLabel: longLabelValidation,
  taxNumber: taxNumberValidation,
  sites: sitesValidation,
  projectInvolvementType: projectInvolvementTypeValidation,
};

export function validate(
  key: keyof CompanyOnView,
  value: CompanyOnView[keyof CompanyOnView],
  allValues?: CompanyOnView
): ErrorPresentation {
  return validation[key](value, allValues);
}

export function handleFormValidation(
  errorsSetter: Dispatch<SetStateAction<HashMap<ErrorPresentation>>>,
  form: CompanyOnView
): boolean {
  const errors = Object.entries(form).reduce<HashMap<ErrorPresentation>>(
    (errors, [key, value]) => {
      const error = validate(key as keyof CompanyOnView, value, form);
      if (error) {
        errors[key] = error;
      }
      return errors;
    },
    {}
  );
  errorsSetter(errors);
  return Object.keys(errors).length > 0;
}

function shortLabelValidation(value: any): ErrorPresentation {
  return (
    requiredValidation(value) || stringMinMaxValidation(FieldSize.S, value)
  );
}

function longLabelValidation(value: any): ErrorPresentation {
  return (
    requiredValidation(value) || stringMinMaxValidation(FieldSize.M, value)
  );
}

function taxNumberValidation(value: any): ErrorPresentation {
  return stringMaxValidation(FieldSize.S, value);
}

function sitesValidation(value: any): ErrorPresentation {
  return requiredValidation(value) || notEmptyArrayValidation(value);
}

function projectInvolvementTypeValidation(value: any): ErrorPresentation {
  return requiredValidation(value);
}
